import { useState } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Loader2, CheckCircle, KeyRound, User, Mail, Shield } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { ProviderLayout } from './ProviderLayout';

export function ProviderSettings() {
  const { authHeaders, API, user } = useAdminAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    if (form.next !== form.confirm) { setError('Neue Passwörter stimmen nicht überein'); return; }
    if (form.next.length < 8) { setError('Neues Passwort muss mindestens 8 Zeichen haben'); return; }
    setLoading(true);
    try {
      await axios.put(
        `${API}/admin/me/password`,
        { current_password: form.current, new_password: form.next },
        { headers: authHeaders() }
      );
      setSuccess(true);
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-2.5 pr-10 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 transition-colors";
  const labelCls = "text-xs font-medium text-slate-400 uppercase tracking-wider block mb-1.5";

  return (
    <ProviderLayout>
      <div className="p-4 sm:p-6 md:p-8 w-full">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Einstellungen</h1>
          <p className="text-sm text-slate-500 mt-0.5">Konto &amp; Sicherheit</p>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

          {/* Left: Konto-Info + Sicherheitstipps */}
          <div className="xl:col-span-2 space-y-5">

            {/* Konto-Informationen */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
              <h2 className="text-sm font-semibold text-slate-300 pb-3 border-b border-slate-800 mb-4">Konto-Informationen</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Name</p>
                    <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Mail className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">E-Mail</p>
                    <p className="text-sm text-white truncate">{user?.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Shield className="w-4 h-4 text-cyan-400" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">Rolle</p>
                    <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-cyan-500/15 text-cyan-400 border border-cyan-500/20">
                      Anbieter
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Sicherheits-Hinweise */}
            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
              <h2 className="text-sm font-semibold text-slate-300 pb-3 border-b border-slate-800 mb-4">Sicherheitshinweise</h2>
              <ul className="space-y-2.5 text-sm text-slate-400">
                {[
                  'Verwende mindestens 8 Zeichen mit Groß-/Kleinbuchstaben und Zahlen.',
                  'Teile dein Passwort niemals mit Dritten.',
                  'Nutze ein einzigartiges Passwort, das du nicht für andere Dienste verwendest.',
                  'Bei Verdacht auf unbefugten Zugriff sofort Passwort ändern.',
                ].map((tip, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-500/60 flex-shrink-0 mt-1.5" />
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Passwort ändern */}
          <div className="xl:col-span-3">
            <div className="bg-[#0f172a] border border-slate-800 rounded-xl p-6">
              <div className="flex items-center gap-2 pb-3 border-b border-slate-800 mb-6">
                <KeyRound className="w-4 h-4 text-cyan-400" />
                <h2 className="text-sm font-semibold text-slate-300">Passwort ändern</h2>
              </div>

              {success && (
                <div data-testid="provider-password-change-success"
                  className="flex items-center gap-2 px-4 py-3 bg-green-500/10 border border-green-500/30 rounded-lg text-sm text-green-400 mb-6">
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                  Passwort erfolgreich geändert
                </div>
              )}

              {error && (
                <div data-testid="provider-password-change-error"
                  className="px-4 py-2.5 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400 mb-6">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="provider-change-password-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

                  {/* Aktuelles Passwort – volle Breite */}
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Aktuelles Passwort</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={form.current}
                        onChange={e => set('current', e.target.value)}
                        required
                        data-testid="provider-current-password"
                        placeholder="Dein aktuelles Passwort"
                        className={inputCls}
                      />
                      <button type="button" onClick={() => setShowPw(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors">
                        {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Neues + Bestätigen – je halbe Breite ab sm */}
                  <div>
                    <label className={labelCls}>Neues Passwort</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={form.next}
                        onChange={e => set('next', e.target.value)}
                        required
                        data-testid="provider-new-password"
                        placeholder="Min. 8 Zeichen"
                        className={inputCls}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls}>Passwort bestätigen</label>
                    <div className="relative">
                      <input
                        type={showPw ? 'text' : 'password'}
                        value={form.confirm}
                        onChange={e => set('confirm', e.target.value)}
                        required
                        data-testid="provider-confirm-password"
                        placeholder="Passwort wiederholen"
                        className={inputCls}
                      />
                    </div>
                  </div>
                </div>

                {/* Passwort-Stärke-Indikator */}
                {form.next.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="flex gap-1">
                      {[1,2,3,4].map(lvl => {
                        const strength = Math.min(
                          (form.next.length >= 8 ? 1 : 0) +
                          (/[A-Z]/.test(form.next) ? 1 : 0) +
                          (/[0-9]/.test(form.next) ? 1 : 0) +
                          (/[^A-Za-z0-9]/.test(form.next) ? 1 : 0), 4
                        );
                        return (
                          <div key={lvl} className={`h-1 flex-1 rounded-full transition-colors ${
                            lvl <= strength
                              ? strength <= 1 ? 'bg-red-500' : strength === 2 ? 'bg-amber-500' : strength === 3 ? 'bg-yellow-400' : 'bg-green-500'
                              : 'bg-slate-700'
                          }`} />
                        );
                      })}
                    </div>
                    <p className="text-xs text-slate-500">
                      {(() => {
                        const s = Math.min(
                          (form.next.length >= 8 ? 1 : 0) +
                          (/[A-Z]/.test(form.next) ? 1 : 0) +
                          (/[0-9]/.test(form.next) ? 1 : 0) +
                          (/[^A-Za-z0-9]/.test(form.next) ? 1 : 0), 4
                        );
                        return ['Zu schwach', 'Schwach', 'Mittel', 'Stark', 'Sehr stark'][s];
                      })()}
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="provider-change-password-submit"
                  className="w-full sm:w-auto px-8 py-2.5 bg-cyan-500 text-slate-950 font-semibold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2 text-sm"
                >
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                  Passwort speichern
                </button>
              </form>
            </div>
          </div>

        </div>
      </div>
    </ProviderLayout>
  );
}

